import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Grid, Typography, makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import { Steps } from "../../components/StepBar";
import { COLORS } from "../../../../theme/colors";
import { DEFAULT_HEADERS } from "../../constants";
import { BuildingsTable } from "../../components/BuildingsTable";
import { BouncingDotsLoader } from "../../../../components/bouncingDotsLoader";
import { DEFAULT_DRAFT_URL, GLOBAL_DRAFT_URL } from "../../../aroundtown/PortfolioRegistrationPage";
import { getData } from "../../../../services";
import { READY_STATUSES } from "../../../nycMalls/PortfolioRegistrationPage";
import { PreliminaryStageSolarForecast } from "../../../registration/tableHeaders";

export const useStyles = makeStyles((theme) => ({
  root: {
    padding: 20,
    maxWidth: 500,
    margin: "auto",
  },
  calculatedRow: {
    marginBottom: theme.spacing(2),
    columnGap: theme.spacing(2),
    flexWrap: "nowrap",
  },
}));

const defaultCalculatedValues = [
  "Buildings Address",
  "Buildings Area",
  "Segment",
  "Number of Units & Tenants",
  "Total Electricity Costs",
  "Electricity Consumption",
];

const onlyRoofValues = [
  "Roof - Estimated Installed Capacity (kWp)",
  "Roof - Annual Electricity Production (kWh/Year)",
];

const roofCalculatedValues = [
  ...defaultCalculatedValues,
  "Annual photovoltaic power output (kWh/kWp)",
  ...onlyRoofValues,
];

const parkingCalculatedValues = [
  ...defaultCalculatedValues,
  "Annual photovoltaic power output (kWh/kWp)",
  ...onlyRoofValues,
  "Parking Lots - Estimated Installed Capacity (kWp)",
  "Parking Lots - Annual Electricity Production (kWh/Year)",
];

export const CalculatingComponent = ({ rowsCount, setStep, setRows }) => {
  const classes = useStyles();
  const [calculatingIndex, setCalculatingIndex] = useState(0);
  const { user, clusters } = useSelector((state) => state.auth);
  const preliminaryStage = clusters[0]?.preliminary_stage;

  const calculatedValues = () => {
    if (preliminaryStage?.solar_forecast === PreliminaryStageSolarForecast.without_parking_lots) {
      return roofCalculatedValues;
    } else if (preliminaryStage?.solar_forecast === PreliminaryStageSolarForecast.parking_lots) {
      return parkingCalculatedValues;
    }
    return defaultCalculatedValues;
  };

  useEffect(() => {
    let intervalId;
    if (Object.values(PreliminaryStageSolarForecast).includes(preliminaryStage?.solar_forecast)) {
      intervalId = setInterval(() => {
        getData(`${GLOBAL_DRAFT_URL}task/`, (data) => {
          if (READY_STATUSES.includes(data.status)) {
            clearInterval(intervalId);
            localStorage.setItem(
              `step-${user.cluster_registration_flow}-uid${user.id}`,
              Steps.reviewCalculation
            );
            setStep(Steps.reviewCalculation);
            setRows(data.buildings);
          }
        }).catch((error) => {
          if (error?.response?.status === 400) {
            setCalculatingIndex((prev) => {
              if (prev < calculatedValues().length - 1) {
                return prev + 1;
              }
              return calculatedValues().length - 1;
            });
          }
        });
      }, 1000);
    } else {
      intervalId = setInterval(() => {
        setCalculatingIndex((calculatingIndex) => {
          if (calculatingIndex === calculatedValues().length - 1) {
            clearInterval(intervalId);
            localStorage.setItem(
              `step-${user.cluster_registration_flow}-uid${user.id}`,
              Steps.reviewCalculation
            );
            setStep(Steps.reviewCalculation);
          }
          return calculatingIndex + 1;
        });
      }, (rowsCount / calculatedValues().length) * 200);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <Grid
      className={classes.root}
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Typography align="center" variant="h4" style={{ marginBottom: 40 }}>
        Processing Data....
      </Typography>
      {calculatedValues().map((t, i) => (
        <Grid className={classes.calculatedRow} key={i} container justifyContent="space-between">
          <Typography
            variant="h4"
            style={{ color: i > calculatingIndex ? COLORS.darks[0][50] : " initial" }}
          >
            {t}
          </Typography>
          {i < calculatingIndex ? (
            <CheckCircleIcon style={{ color: COLORS.green }} />
          ) : i === calculatingIndex ? (
            <BouncingDotsLoader />
          ) : (
            <FiberManualRecordIcon style={{ color: COLORS.darks[0][25] }} />
          )}
        </Grid>
      ))}
    </Grid>
  );
};

export const ConfirmStep = ({
  rows,
  setRows,
  disableTable,
  deleteBuilding,
  isDataConfirmed,
  setStep,
  headers = DEFAULT_HEADERS,
  draftUrl = DEFAULT_DRAFT_URL,
}) => {
  if (isDataConfirmed) {
    return (
      <Box borderRadius={8} bgcolor={COLORS.darks[0][10]} height="100%" minHeight={300}>
        <CalculatingComponent rowsCount={rows.length} setStep={setStep} setRows={setRows} />
      </Box>
    );
  }
  return (
    <BuildingsTable
      rows={rows}
      setRows={setRows}
      disableTable={disableTable}
      deleteBuilding={deleteBuilding}
      headers={headers}
      draftUrl={draftUrl}
    />
  );
};
